import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Link2, Loader2, UserPlus } from 'lucide-react';
import TranscriptExtractedTasksPanel from './TranscriptExtractedTasksPanel';
import EditTranscriptParticipantModal from './EditTranscriptParticipantModal';
import AddTranscriptParticipantModal from './AddTranscriptParticipantModal';

interface ParticipantSubscriber {
  email: string;
  subscriberId: string | null;
  loading: boolean;
}

interface TranscriptTasksTabProps {
  transcriptId: string;
  summary: string | null;
  participants: string[] | null;
  participantSubscribers: Map<string, ParticipantSubscriber>;
  onConvertToSubscriber: (participant: string) => void;
  onParticipantsUpdated?: () => void;
  onTasksChanged?: () => void;
}

const TranscriptTasksTab: React.FC<TranscriptTasksTabProps> = ({
  transcriptId,
  summary,
  participants,
  participantSubscribers,
  onConvertToSubscriber,
  onParticipantsUpdated,
  onTasksChanged,
}) => {
  const [editingParticipantIndex, setEditingParticipantIndex] = useState<number | null>(null);
  const [addingParticipant, setAddingParticipant] = useState(false);
  const participantList = participants ?? [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start">
      <div className="space-y-5 min-w-0">
        <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 p-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Who attended
            </h2>
            <button
              type="button"
              onClick={() => setAddingParticipant(true)}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
            >
              <UserPlus className="w-3 h-3" />
              Add person
            </button>
          </div>
          {participantList.length ? (
            <ul className="space-y-2">
              {participantList.map((p, i) => {
                const subscriber = participantSubscribers.get(p);
                const hasEmail = !!subscriber;
                const isLinked = subscriber?.subscriberId != null;
                const isLoading = subscriber?.loading || false;
                const displayName = p.split(' <')[0]?.trim() || p;

                return (
                  <li
                    key={`${i}-${p.slice(0, 24)}`}
                    className="flex items-center justify-between gap-2 py-1.5 border-b border-gray-100 dark:border-gray-700/80 last:border-0"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{displayName}</p>
                      {hasEmail ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{subscriber.email}</p>
                      ) : (
                        <p className="text-xs text-amber-600 dark:text-amber-400 italic">No email linked</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => setEditingParticipantIndex(i)}
                        title={hasEmail ? 'Edit email or link to CRM' : 'Set email or link to CRM'}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60"
                      >
                        <Link2 className="w-3 h-3" />
                        {hasEmail ? 'Edit' : 'Link'}
                      </button>
                      {hasEmail ? (
                        isLinked ? (
                          <Link
                            to={`/subscribers/${subscriber.subscriberId}`}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                          >
                            <CheckCircle className="w-3 h-3" />
                            CRM
                          </Link>
                        ) : (
                          <button
                            type="button"
                            onClick={() => onConvertToSubscriber(p)}
                            disabled={isLoading}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md bg-blue-600 text-white disabled:opacity-50"
                          >
                            {isLoading ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <UserPlus className="w-3 h-3" />
                            )}
                            Add
                          </button>
                        )
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">No participants recorded.</p>
          )}
        </section>

        <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 p-5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Summary
          </h2>
          {summary?.trim() ? (
            <p className="mt-2 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
              {summary.trim()}
            </p>
          ) : (
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 italic">No summary for this meeting.</p>
          )}
        </section>
      </div>

      <TranscriptExtractedTasksPanel
        transcriptId={transcriptId}
        onTasksChanged={onTasksChanged}
        className="min-w-0"
      />

      {editingParticipantIndex != null && participantList.length ? (
        <EditTranscriptParticipantModal
          isOpen
          transcriptId={transcriptId}
          participantIndex={editingParticipantIndex}
          participants={participantList}
          onClose={() => setEditingParticipantIndex(null)}
          onSaved={() => {
            setEditingParticipantIndex(null);
            onParticipantsUpdated?.();
          }}
        />
      ) : null}

      <AddTranscriptParticipantModal
        isOpen={addingParticipant}
        transcriptId={transcriptId}
        participants={participantList}
        onClose={() => setAddingParticipant(false)}
        onSaved={() => {
          setAddingParticipant(false);
          onParticipantsUpdated?.();
        }}
      />
    </div>
  );
};

export default TranscriptTasksTab;
